import { Component, Input, inject } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import {
  Advertiser,
  CampaignsCampaignAdminApiClient,
  ConsumerDataConnection,
  ConsumptionAuthorization,
  DataConnectionStatus,
  DataConnectionType,
} from 'src/app/api/api-campaigns.service.generated';
import { LoadingAwareComponent } from 'src/app/common/utils/loading-aware.component';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  FormsModule,
} from '@angular/forms';
import {
  connectionTemplateOptions,
  connectionTemplates,
} from 'src/app/const/template';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressBar } from "@angular/material/progress-bar";
import { ButtonComponent } from "wecheer-common";

@Component({
  selector: 'app-advertiser-consumer-data-connection',
  templateUrl: './advertiser-consumer-data-connection.component.html',
  styleUrls: ['./advertiser-consumer-data-connection.component.scss'],
  imports: [
    ReactiveFormsModule,
    FormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    MatInputModule,
    MatButtonModule,
    MatProgressBar,
    ButtonComponent
]
})
export class AdvertiserConsumerDataConnectionComponent extends LoadingAwareComponent {
  private fb = inject(FormBuilder);
  private campaignsClient = inject(CampaignsCampaignAdminApiClient);

  _advertiser: Advertiser;
  get advertiser() {
    return this._advertiser;
  }
  @Input() set advertiser(value: Advertiser) {
    this._advertiser = value;
    this.selectedTemplate =
      (value?.dataConnection as any)?.connectionTemplate || 'BatCH';
    this.loadTemplateFields(this.selectedTemplate);
    this.subscribeToFormChanges();
    this.refresh();
  }

  connectionTemplates = connectionTemplateOptions;
  selectedTemplate: string = 'BatCH'; // Default template selection
  validConnection = null;
  oldPassword = null;
  dynamicFields: any[] = [];
  form: FormGroup = new FormGroup({});
  consumptionAuthorization: ConsumptionAuthorization = ConsumptionAuthorization.fromJS({
    consumptionApiKey: null,
    consumptionApiSecret: null,
  })

  ngOnInit(): void { }

  private subscribeToFormChanges(): void {
    // Dynamically get the form control names from the dynamic fields array
    const formControlNames = this.dynamicFields.map(
      (field) => field.formControlName
    );

    // Subscribe to value changes for each form control
    formControlNames.forEach((controlName) => {
      // Ensure the form control exists before subscribing
      const control = this.form.get(controlName);
      if (control) {
        control.valueChanges.subscribe(() => {
          this.validConnection = false; // Reset the connection validity when any field changes
        });
      }
    });
  }

  async refresh() {
    if (!this.advertiser) return;

    try {
      // Fetch connection data based on the advertiser
      const connection = await lastValueFrom(
        this.campaignsClient.getAdvertiserDataConnections(this.advertiser.id)
      );

      if (!connection) {
        this.validConnection = false;
        return;
      }

      // Build form patch object for all dynamic fields at once
      const formPatch: { [key: string]: any } = {};

      this.dynamicFields.forEach((field) => {
        const value = connection.data?.[field.formControlName];
        if (value !== undefined) {
          formPatch[field.formControlName] = value;
        }
      });

      // Handle template-specific logic
      if (this.selectedTemplate === 'BatCH' && connection.data) {
        if (connection.data.password) {
          this.oldPassword = connection.data.password;
        }
        if (connection.data.type) {
          formPatch.type = connection.data.type as DataConnectionType;
        }
      }

      // Apply all form patches at once
      if (Object.keys(formPatch).length > 0) {
        this.form.patchValue(formPatch);
      }

      // Update consumption authorization
      if (connection.consumptionAuthorization) {
        this.consumptionAuthorization = connection.consumptionAuthorization;
      }

      // Set connection validity
      this.validConnection = connection.status === DataConnectionStatus.Verified;
    } catch (error) {
      console.error('Error refreshing data connection:', error);
      this.validConnection = false;
    }
  }

  async onSave() {
    // Mark the form as touched to trigger validation for all controls
    this.form.markAsTouched();

    if (this.form.valid) {
      // Initialize the data object with the fixed fields: connectionTemplate and status
      const data: any = {
        connectionTemplate: this.selectedTemplate, // Fixed field
        status: this.validConnection
          ? DataConnectionStatus.Verified
          : DataConnectionStatus.NotVerified, // Fixed field
        data: {}, // Initialize the dynamic data object
      };

      // Collect dynamic fields and add them under the 'data' object
      Object.keys(this.form.value).forEach((field) => {
        // Exclude the fixed fields (connectionTemplate and status)
        if (field !== 'connectionTemplate' && field !== 'status') {
          data.data[field] = this.form.value[field]; // Add dynamic fields under data.data
        }
      });

      // Send the data to the server
      try {
        await lastValueFrom(
          this.campaignsClient.updateAdvertiserDataConnection(
            this.advertiser.id,
            data
          )
        );
      } catch (error) {
        console.error('Error saving data connection:', error);
      }
    }
  }

  async onSaveConsumptionAuth() {
    if (
      this.consumptionAuthorization.consumptionApiKey &&
      this.consumptionAuthorization.consumptionApiSecret
    ) {
      const data: any = {
        consumptionAuthorization: this.consumptionAuthorization,
      };

      // If connection is verified, include the template data
      if (this.validConnection && this.form.valid) {
        data.connectionTemplate = this.selectedTemplate;
        data.status = DataConnectionStatus.Verified;
        data.data = {};

        Object.keys(this.form.value).forEach((field) => {
          if (field !== 'connectionTemplate' && field !== 'status') {
            data.data[field] = this.form.value[field];
          }
        });
      }

      try {
        await lastValueFrom(
          this.campaignsClient.updateAdvertiserDataConnection(
            this.advertiser.id,
            data
          )
        );
      } catch (error) {
        console.error('Error saving consumption authorization:', error);
      }
    }
  }

  async testConnection() {
    // Mark the form as touched to trigger validation for all controls
    this.form.markAsTouched();

    // If the form is valid
    if (this.form.valid) {
      // Prepare the dynamic data object based on dynamic fields
      const data: any = {};

      // Iterate through dynamic fields and populate the data object
      this.dynamicFields.forEach((field) => {
        if (
          field.formControlName === 'password' &&
          this.oldPassword !== this.form.value.password
        ) {
          // Include the password only if it has changed
          data[field.formControlName] = this.form.value[field.formControlName];
        } else if (field.formControlName !== 'password') {
          // For all other fields, just add the form value to the data object
          data[field.formControlName] = this.form.value[field.formControlName];
        }
      });

      const dataConnection = ConsumerDataConnection.fromJS({
        data,
        connectionTemplate: this.selectedTemplate,
      });

      try {
        // Call the testAdvertiserDataConnections API with the constructed data
        const result = await lastValueFrom(
          this.campaignsClient.testAdvertiserDataConnections(
            this.advertiser.id,
            dataConnection
          )
        );

        // Update the connection validity status
        this.validConnection = result.result;

        // Update the form status field based on the result
        this.form.patchValue({
          status: result.result
            ? DataConnectionStatus.Verified
            : DataConnectionStatus.NotVerified,
        });
      } catch (error) {
        // Handle error if the connection test fails (e.g., show an error message)
        console.error('Connection test failed', error);
        this.validConnection = false;
        this.form.patchValue({
          status: DataConnectionStatus.NotVerified,
        });
      }
    }
  }

  onTemplateChange(templateName: string) {
    this.selectedTemplate = templateName;
    this.loadTemplateFields(templateName);
    this.subscribeToFormChanges();
    this.refresh();
  }

  loadTemplateFields(templateName: string) {
    // Get the selected template's fields from the service
    this.dynamicFields = connectionTemplates[templateName];

    this.consumptionAuthorization = ConsumptionAuthorization.fromJS({
      consumptionApiKey: null,
      consumptionApiSecret: null,
    });

    // Dynamically build the form controls
    const controls = {};
    this.dynamicFields.forEach((field) => {
      // Add validators based on the 'required' property of the field
      controls[field.formControlName] = field.required
        ? new FormControl(null, Validators.required)
        : new FormControl(null);
    });

    // Recreate the FormGroup based on the dynamic fields
    this.form = this.fb.group(controls);
  }
}
